import { storage } from '@abyss/web/tools/storage';
import { Flex } from '@abyss/web/ui/Flex';
import { Popover } from '@abyss/web/ui/Popover';
import { ToggleSwitch } from '@abyss/web/ui/ToggleSwitch';
import { useEffect } from 'react';

import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useHsidUuid } from '../../hooks/useHsidUuid';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { getUiMessageFromLagoon } from '../../utils/common';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import PopoverContent from '../PSXHeader/SmartChoice/PopoverContent';
import {
  StyledText,
  compareSharePopoverStyles,
} from './CompareShareToggle.styled';

interface Props {
  title: string | '';
  isChecked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  dataTestId?: string;
  expandedProviderListView?: boolean;
}

export const CompareShareToggle = ({
  title,
  isChecked,
  onChange,
  dataTestId,
  expandedProviderListView = false,
}: Props) => {
  const lagoonData = useLagoon('ui-messaging')();
  const compareProvidersText = getUiMessageFromLagoon(
    ConstantsLagoon.UI_MESSAGING.COMPARE_PROVIDERS,
    lagoonData
  );
  const getAndShareInformationOnCostText = getUiMessageFromLagoon(
    ConstantsLagoon.UI_MESSAGING.GET_AND_SHARE_INFORMATION_ON_COST,
    lagoonData
  );
  const [isNewComparePopupFlowEnabled] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_COMPARE_POPOVER_FLOW,
  ]);
  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );
  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );
  const hsidUuid = useHsidUuid();
  const [getEducationalContentStatus, setEducationalContentStatus] =
    useSaveEducationalContentStatus({});
  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;

  useEffect(() => {
    if (educationalContentStore?.showCompareCoachmarkWeb) {
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
          activeElement.blur();
        }
      }, 0);
    }
  }, [educationalContentStore?.showCompareCoachmarkWeb]);

  const handleOpenChange = () => {
    setEducationalContentStatus({
      variables: {
        hasViewedContent: !!educationalContentStore?.hasViewedContent,
        showCompareCoachmarkWeb: false,
        ...(hsidUuid && { userId: hsidUuid }),
        isSuperUser,
      },
    });
    sessionStorage.removeItem(
      Constants.STORAGE_KEYS.SESSION.HAS_DISMISSED_COMPARE_COACHMARK_WEB
    );
  };

  useEffect(() => {
    if (getEducationalContentStatus?.data?.saveEducationalContentStatus) {
      const { saveEducationalContentStatus } =
        getEducationalContentStatus?.data;
      setEducationalContentStore({
        hasViewedContent: saveEducationalContentStatus?.hasViewedContent,
        showCompareModal:
          saveEducationalContentStatus?.showCompareModal === null
            ? educationalContentStore?.showCompareModal
            : saveEducationalContentStatus?.showCompareModal,
        showCompareCoachmarkWeb:
          saveEducationalContentStatus?.showCompareCoachmarkWeb,
      });
    }
  }, [getEducationalContentStatus]);
  const isGEHAV2 = shouldUseGehaV2Styling();
  const showCompareCoachMark = () => {
    const hasDismissed =
      sessionStorage.getItem(
        Constants.STORAGE_KEYS.SESSION.HAS_DISMISSED_COMPARE_COACHMARK_WEB
      ) !== 'y';
    const shouldShowCoachMark =
      educationalContentStore?.showCompareCoachmarkWeb;
    const isCompareModalHidden = !educationalContentStore?.showCompareModal;

    return isNewComparePopupFlowEnabled
      ? hasDismissed && shouldShowCoachMark && isCompareModalHidden
      : shouldShowCoachMark;
  };

  return (
    <Popover
      content={
        <PopoverContent
          SC_COACHMARK={{
            title: compareProvidersText,
            message: getAndShareInformationOnCostText,
          }}
          onCloseCoachmarkWeb={handleOpenChange}
        />
      }
      css={{
        ...compareSharePopoverStyles,
        '@media print': {
          display: 'none !important',
        },
      }}
      data-testid="popover"
      open={showCompareCoachMark()}
      position="bottom"
      showClose={false}
      style={{ zIndex: 3 }}
    >
      <Flex>
        <StyledText
          color={isGEHAV2 ? '$primary1' : '$black'}
          data-testid={`${dataTestId}-title`}
        >
          {title}
        </StyledText>
        <ToggleSwitch
          data-testid={dataTestId}
          isChecked={isChecked}
          onChange={(event) => {
            if (event.target.checked && isNewComparePopupFlowEnabled) {
              handleOpenChange();
            }
            onChange(event);
          }}
          size={expandedProviderListView ? 'md' : 'sm'}
        ></ToggleSwitch>
      </Flex>
    </Popover>
  );
};
